import { alpha } from '@mui/material/styles';

export const brand = {
    50: 'hsl(217, 100%, 96%)',
    100: 'hsl(217, 100%, 92%)',
    200: 'hsl(217, 95%, 80%)',
    300: 'hsl(217, 90%, 65%)',
    400: 'hsl(217, 85%, 50%)',
    500: 'hsl(217, 95%, 20%)',  // Dark navy blue - #1A237E
    600: 'hsl(217, 95%, 18%)',
    700: 'hsl(217, 95%, 15%)',  // Darker navy - #0D1B2A
    800: 'hsl(217, 95%, 12%)',
    900: 'hsl(217, 95%, 10%)',
};

export const gray = {
    50: 'hsl(220, 35%, 97%)',
    100: 'hsl(220, 30%, 94%)',
    200: 'hsl(220, 20%, 88%)',
    300: 'hsl(220, 20%, 80%)',
    400: 'hsl(220, 20%, 65%)',
    500: 'hsl(220, 20%, 42%)',
    600: 'hsl(220, 20%, 35%)',
    700: 'hsl(220, 20%, 25%)',
    800: 'hsl(220, 30%, 6%)',
    900: 'hsl(220, 35%, 3%)',
};

export const green = {
    50: 'hsl(120, 80%, 98%)',
    100: 'hsl(120, 75%, 94%)',
    200: 'hsl(120, 75%, 87%)',
    300: 'hsl(120, 61%, 77%)',
    400: 'hsl(120, 44%, 53%)',
    500: 'hsl(120, 59%, 30%)',
    600: 'hsl(120, 70%, 25%)',
    700: 'hsl(120, 75%, 16%)',
    800: 'hsl(120, 84%, 10%)',
    900: 'hsl(120, 87%, 6%)',
};

export const orange = {
    50: 'hsl(45, 100%, 97%)',
    100: 'hsl(45, 92%, 90%)',
    200: 'hsl(45, 94%, 80%)',
    300: 'hsl(45, 90%, 65%)',
    400: 'hsl(45, 90%, 40%)',
    500: 'hsl(45, 90%, 35%)',
    600: 'hsl(45, 91%, 25%)',
    700: 'hsl(45, 94%, 20%)',
    800: 'hsl(45, 95%, 16%)',
    900: 'hsl(45, 93%, 12%)',
};

export const red = {
    50: 'hsl(0, 100%, 97%)',
    100: 'hsl(0, 92%, 90%)',
    200: 'hsl(0, 94%, 80%)',
    300: 'hsl(0, 90%, 65%)',
    400: 'hsl(0, 90%, 40%)',
    500: 'hsl(0, 90%, 30%)',
    600: 'hsl(0, 91%, 25%)',
    700: 'hsl(0, 94%, 18%)',
    800: 'hsl(0, 95%, 12%)',
    900: 'hsl(0, 93%, 6%)',
};

export const teal = {
    50: 'hsl(180, 80%, 98%)',
    100: 'hsl(180, 75%, 94%)',
    200: 'hsl(180, 75%, 87%)',
    300: 'hsl(180, 61%, 77%)',
    400: 'hsl(180, 50%, 50%)',
    500: 'hsl(180, 60%, 40%)',
    600: 'hsl(180, 70%, 30%)',
    700: 'hsl(180, 75%, 20%)',
    800: 'hsl(180, 84%, 12%)',
    900: 'hsl(180, 87%, 8%)',
};

export const redOrange = {
    50: 'hsl(15, 100%, 97%)',
    100: 'hsl(15, 95%, 92%)',
    200: 'hsl(15, 94%, 85%)',
    300: 'hsl(15, 90%, 70%)',
    400: 'hsl(15, 100%, 55%)',  // #FF6B35 - main red-orange
    500: 'hsl(15, 95%, 45%)',
    600: 'hsl(15, 90%, 35%)',
    700: 'hsl(15, 85%, 28%)',
    800: 'hsl(15, 80%, 20%)',
    900: 'hsl(15, 75%, 15%)',
};

export const colorSchemes = {
    light: {
        palette: {
            primary: {
                light: 'hsl(195, 55%, 55%)',  // Lighter cyan-blue
                main: 'hsl(195, 60%, 45%)',   // Cyan-blue - more blue than green
                dark: 'hsl(195, 65%, 35%)',   // Darker cyan-blue
                contrastText: '#ffffff',
            },
            info: {
                light: brand[100],
                main: brand[300],
                dark: brand[600],
                contrastText: gray[50],
            },
            warning: {
                light: orange[300],
                main: orange[400],
                dark: orange[800],
            },
            error: {
                light: red[300],
                main: red[400],
                dark: red[800],
            },
            success: {
                light: green[300],
                main: green[400],
                dark: green[800],
            },
            teal: {
                light: teal[300],
                main: teal[400],
                dark: teal[600],
                contrastText: '#ffffff',
            },
            redOrange: {
                light: redOrange[300],
                main: redOrange[400],
                dark: redOrange[600],
                contrastText: '#ffffff',
            },
            grey: {
                ...gray,
            },
            divider: alpha(gray[300], 0.4),
            background: {
                default: 'hsl(0, 0%, 99%)',
                paper: 'hsl(220, 35%, 97%)',
            },
            text: {
                primary: gray[800],
                secondary: gray[600],
                warning: orange[400],
            },
            action: {
                hover: alpha(gray[200], 0.2),
                selected: `${alpha(gray[200], 0.3)}`,
            },
            baseShadow:
                'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
        },
    },
    dark: {
        palette: {
            primary: {
                contrastText: '#ffffff',
                light: brand[200],
                main: brand[300],  // Brighter blue for dark mode - 65% lightness
                dark: brand[400],  // Medium blue for hover states
            },
            info: {
                contrastText: '#ffffff',
                light: brand[200],
                main: brand[300],
                dark: brand[400],
            },
            warning: {
                light: orange[400],
                main: orange[500],
                dark: orange[700],
            },
            error: {
                light: '#ff8a80',  // Lighter red for dark mode
                main: '#ff6b6b',   // Brighter red for dark mode
                dark: '#ff5252',   // Darker variant of bright red
                contrastText: '#ffffff',  // White text for contrast
            },
            success: {
                light: green[400],
                main: green[500],
                dark: green[700],
            },
            teal: {
                light: teal[400],
                main: teal[500],
                dark: teal[700],
                contrastText: '#ffffff',
            },
            redOrange: {
                light: redOrange[400],
                main: redOrange[500],
                dark: redOrange[700],
                contrastText: '#ffffff',
            },
            grey: {
                ...gray,
            },
            divider: alpha(gray[700], 0.6),
            background: {
                default: 'hsl(220, 18.80%, 12.50%)',
                paper: 'hsl(220, 20.00%, 14.70%)',
            },
            text: {
                primary: 'hsl(0, 0%, 100%)',
                secondary: gray[400],
            },
            action: {
                hover: alpha(gray[600], 0.2),
                selected: alpha(gray[600], 0.3),
            },
            baseShadow:
                'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
        },
    },
};

export const shape = {
    borderRadius: 8,
};

// Enhanced responsive breakpoints
export const breakpoints = {
    xs: 0,    // Extra small devices (phones, 0px and up)
    sm: 600,  // Small devices (tablets, 600px and up)
    md: 900,  // Medium devices (small laptops, 900px and up)
    lg: 1200, // Large devices (desktops, 1200px and up)
    xl: 1536, // Extra large devices (large desktops, 1536px and up)
};

export const custom = {
  gradients: {
    hero: 'linear-gradient(120deg, #f8fafc 0%, #e3e8ee 100%)',
    primary: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
    beauty: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
    moving: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
    tailoring: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
    event: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
    tutoring: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
    pet: 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)',
    training: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
    photo: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
  },
  borderRadius: {
    hero: 32,
    card: 16,
    paper: 8,
    chip: 8,
    avatar: 50,
  },
  shadows: {
    hero: '0 2px 16px 0 rgba(60,72,88,0.04)',
    card: '0 2px 12px 0 rgba(60,72,88,0.07)',
    cardHover: '0 4px 24px 0 rgba(60,72,88,0.12)',
    chip: '0 2px 8px 0 rgba(60,72,88,0.06)',
  },
  // Responsive spacing utilities
  spacing: {
    mobile: {
      xs: 1,    // 8px
      sm: 2,    // 16px
      md: 3,    // 24px
      lg: 4,    // 32px
      xl: 6,    // 48px
    },
    tablet: {
      xs: 1.5,  // 12px
      sm: 2.5,  // 20px
      md: 4,    // 32px
      lg: 6,    // 48px
      xl: 8,    // 64px
    },
    desktop: {
      xs: 2,    // 16px
      sm: 3,    // 24px
      md: 5,    // 40px
      lg: 8,    // 64px
      xl: 12,   // 96px
    },
  },
  // Responsive typography
  typography: {
    mobile: {
      h1: { fontSize: '2rem', lineHeight: 1.2 },
      h2: { fontSize: '1.75rem', lineHeight: 1.3 },
      h3: { fontSize: '1.5rem', lineHeight: 1.4 },
      h4: { fontSize: '1.25rem', lineHeight: 1.4 },
      h5: { fontSize: '1.125rem', lineHeight: 1.5 },
      h6: { fontSize: '1rem', lineHeight: 1.5 },
      body1: { fontSize: '0.875rem', lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
    },
    tablet: {
      h1: { fontSize: '2.5rem', lineHeight: 1.2 },
      h2: { fontSize: '2rem', lineHeight: 1.3 },
      h3: { fontSize: '1.75rem', lineHeight: 1.4 },
      h4: { fontSize: '1.5rem', lineHeight: 1.4 },
      h5: { fontSize: '1.25rem', lineHeight: 1.5 },
      h6: { fontSize: '1.125rem', lineHeight: 1.5 },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    },
    desktop: {
      h1: { fontSize: '3rem', lineHeight: 1.2 },
      h2: { fontSize: '2.5rem', lineHeight: 1.3 },
      h3: { fontSize: '2rem', lineHeight: 1.4 },
      h4: { fontSize: '1.75rem', lineHeight: 1.4 },
      h5: { fontSize: '1.5rem', lineHeight: 1.5 },
      h6: { fontSize: '1.25rem', lineHeight: 1.5 },
      body1: { fontSize: '1.125rem', lineHeight: 1.6 },
      body2: { fontSize: '1rem', lineHeight: 1.6 },
    },
  },
};

